import { useEffect, useRef } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';

const INVALIDATIONS: Record<string, string[]> = {
  kit: ['kits', 'kit', 'dashboard', 'units'],
  marketing: [],
  mirror_unit: ['units', 'orders', 'kits', 'dashboard', 'shipments'],
  stock: ['stock', 'products', 'dashboard', 'planning'],
  shipment: ['shipments', 'orders', 'dashboard', 'units'],
  purchase_invoice: ['invoices', 'stock', 'products', 'dashboard'],
  planning_run: ['planning', 'dashboard', 'alerts'],
  planning_alert: ['alerts', 'planning', 'dashboard'],
  product: ['products', 'stock', 'dashboard'],
  order: ['orders', 'dashboard'],
  bom_version: ['models', 'model'],
  unit: ['units', 'orders', 'kits', 'dashboard']
};

export interface StreamEvent {
  type: string;
  entity: string;
  id?: string | null;
  at?: string;
  data?: Record<string, unknown>;
}

type Listener = (event: StreamEvent) => void;

/**
 * Una sola conexión SSE para toda la pestaña. Antes cada `useEventStream` abría su propio
 * `EventSource`: con el stream en el `AppShell` y en una pantalla, el navegador gastaba dos de
 * las seis conexiones HTTP/1.1 por origen e invalidaba cada query dos veces por evento.
 */
const listeners = new Set<Listener>();
let source: EventSource | null = null;
let queryClient: QueryClient | null = null;

/**
 * Claves a invalidar por evento, con la regla general de `INVALIDATIONS` más los casos que
 * afectan a varias entidades.
 */
function keysFor(event: StreamEvent): string[] {
  const keys = [...(INVALIDATIONS[event.entity] ?? [])];
  if (event.type === 'kit.completed' || event.type === 'unit.packed' || event.type === 'shipment.created') {
    keys.push('kits', 'units');
  }
  return keys;
}

/**
 * Las invalidaciones se agrupan en una ventana corta. Una ráfaga de eventos de la misma entidad
 * —varios kits cruzando el umbral de antigüedad a la vez, o todos los atrasados tras un reinicio
 * del contenedor— invalidaba las mismas queries una vez por evento: con
 * `INVALIDATIONS.kit` (cuatro claves) eran cuatro refetch por kit. Agrupadas, la ráfaga cuesta
 * una sola ronda.
 */
const pendingKeys = new Set<string>();
let flushTimer: ReturnType<typeof setTimeout> | null = null;

function invalidate(qc: QueryClient, event: StreamEvent) {
  for (const key of keysFor(event)) pendingKeys.add(key);
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    const keys = [...pendingKeys];
    pendingKeys.clear();
    for (const key of keys) void qc.invalidateQueries({ queryKey: [key] });
  }, 200);
}

function ensureSource() {
  if (source) return;
  source = new EventSource('/api/events');
  source.onmessage = (message) => {
    try {
      const event = JSON.parse(message.data) as StreamEvent;
      if (event.type === 'connected') return;
      if (queryClient) invalidate(queryClient, event);
      for (const listener of listeners) listener(event);
    } catch { /* evento no parseable */ }
  };
}

function releaseSource() {
  if (listeners.size > 0 || !source) return;
  source.close();
  source = null;
  queryClient = null;
}

/**
 * Suscribe la pantalla al bus SSE de la API (una conexión compartida por pestaña): invalida las
 * queries afectadas e invoca `onEvent` con cada evento, para las pantallas que además necesitan
 * reaccionar (p. ej. el aviso `kit.aged` del puesto de almacén). El callback va en una ref:
 * cambiarlo en cada render no vuelve a suscribirse.
 */
export function useEventStream(enabled: boolean, onEvent?: (event: StreamEvent) => void) {
  const qc = useQueryClient();
  const handler = useRef(onEvent);
  handler.current = onEvent;

  useEffect(() => {
    if (!enabled) return;
    if (typeof EventSource === 'undefined') return;
    queryClient = queryClient ?? qc;
    const listener: Listener = (event) => handler.current?.(event);
    listeners.add(listener);
    ensureSource();
    return () => {
      listeners.delete(listener);
      releaseSource();
    };
  }, [enabled, qc]);
}
